import { html, nothing, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { connect } from 'pwa-helpers';

import { GENERIC_API_ERROR } from '../../../common/constants/api-constants';
import environment from '../../../common/environment';
import { getImageSrcUrl } from '../../../common/utils/product-util';
import store from '../../../store/store';
import { StoreState } from '../../../store/store.interface';
import '../../alert/alert';
import { MessageType } from '../../alert/global-message.interface';
import { BaseComponent } from '../../base-component/base-component';
import { Modal } from '../../modal/modal';
import { IA_COPY, IA_TEST_IDS } from './constants/inclusive-access.constants';
import {
  InclusiveAccessBlock,
  MyDigitalProductAggregatedItem,
} from './interface/my-digital-products-aggregated.interface';
import { getMyDigitalProductsAggregatedData } from './store/my-digital-products-aggregated.selectors';
import {
  OptInOutAction,
  updateMyDigitalProductOptInOut,
} from './thunks/update-my-digital-product-opt-in-out.thunk';
import { formatLongDate, formatShortDate, getPillState, isToggleDisabled } from './utils/inclusive-access.util';

const ANALYTICS_COMPONENT = 'mhecomm-my-digital-products-v2';
const ANALYTICS_AREA = 'main';
const ANALYTICS_SUBAREA = 'body';
const ANALYTICS_EVENTS = {
  toggle: 'my-digital-products-inclusive-access-toggle',
  infoLink: 'my-digital-products-inclusive-access-info-link',
  modalClose: 'my-digital-products-inclusive-access-modal-close',
  modalCancel: 'my-digital-products-inclusive-access-modal-cancel',
  modalConfirm: 'my-digital-products-inclusive-access-modal-confirm',
  selection: 'inclusive-access-selection',
  orderHistoryLink: 'my-digital-products-order-history-link',
  refundPolicyLink: 'my-digital-products-refund-policy-link',
  error: 'my-digital-products-inclusive-access-error',
} as const;

@customElement('mhecomm-inclusive-access-panel')
export class InclusiveAccessPanel extends connect(store)(BaseComponent) {
  @property({ type: String }) isbn10 = '';
  analyticsEmitter?: (name: string, detail: Record<string, unknown>) => void;

  @state() private _item: MyDigitalProductAggregatedItem | undefined;
  @state() private _ia: InclusiveAccessBlock | null = null;
  @state() private _optInOutInFlight = false;
  @state() private _optInOutError = '';

  stateChanged(state: StoreState) {
    if (!this.isbn10) {
      this._item = undefined;
      this._ia = null;
      return;
    }
    const items = getMyDigitalProductsAggregatedData(state);
    const match = items.find((it) => it.identity.isbn10 === this.isbn10);
    this._item = match;
    this._ia = match?.inclusiveAccess ?? null;
  }

  renderComp(): TemplateResult | typeof nothing {
    if (!this._item || !this._ia) {
      return nothing;
    }
    const ia = this._ia;
    const isbn10 = this.isbn10;
    const pill = getPillState(ia, formatShortDate);
    const disabled = isToggleDisabled(ia);
    const toggleLabel = ia.isUserOptedIn ? IA_COPY.toggleOptedIn : IA_COPY.toggleOptedOut;
    const pillClass = 'badge rounded-pill bg-light text-danger text-wrap fw-semibold';
    const toggleStyle = ia.isUserOptedIn ? 'background-color: var(--bs-danger); border-color: var(--bs-danger);' : '';
    const toggleDisabled = disabled || this._optInOutInFlight;

    return html`
      ${this._optInOutError
        ? html`<div class="mb-3" data-testid="${IA_TEST_IDS.error}" data-automation-id="${IA_TEST_IDS.error}-${isbn10}">
            <mhecomm-alert
              type=${MessageType.danger}
              message=${this._optInOutError}
              ?dismissible=${true}></mhecomm-alert>
          </div>`
        : nothing}
      <div
        class="row p-3"
        data-testid="${IA_TEST_IDS.section}"
        data-automation-id="${IA_TEST_IDS.section}-${isbn10}">
        <div class="col-12">
          <div class="d-flex flex-column flex-md-row gap-3 gap-md-5 align-items-start">
            <div>
              <!-- TODO(WECOOL-52349): source from API once the field lands, do not invent a field. -->
              <div class="fw-semibold">${IA_COPY.sectionTitle}</div>
              <button
                type="button"
                class="btn btn-link p-0 align-baseline"
                aria-haspopup="dialog"
                @click=${(event: Event) => this._onWhatIsIAClick(event)}
                >${IA_COPY.whatIsLink}</button
              >
            </div>
            <div>
              <div class="small text-muted">${IA_COPY.availabilityLabel}</div>
              <span
                class="${pillClass}"
                data-testid="${IA_TEST_IDS.pill}"
                data-automation-id="${IA_TEST_IDS.pill}-${isbn10}"
                data-tone="${pill.tone}"
                >${pill.label}</span
              >
            </div>
            <div>
              <div
                class="small text-muted"
                data-testid="${IA_TEST_IDS.updated}"
                data-automation-id="${IA_TEST_IDS.updated}-${isbn10}">
                ${IA_COPY.updatedLabel(formatLongDate(ia.lastUpdatedDate))}
              </div>
              <div class="d-inline-flex align-items-center gap-2 mt-1">
                <div class="form-check form-switch m-0">
                  <input
                    type="checkbox"
                    role="switch"
                    class="form-check-input"
                    style="${toggleStyle}"
                    .checked="${ia.isUserOptedIn}"
                    ?disabled="${toggleDisabled}"
                    aria-checked="${ia.isUserOptedIn ? 'true' : 'false'}"
                    aria-disabled="${toggleDisabled ? 'true' : 'false'}"
                    aria-label="${toggleLabel}"
                    data-testid="${IA_TEST_IDS.toggle}"
                    data-automation-id="${IA_TEST_IDS.toggle}-${isbn10}"
                    @click="${(e: Event) => this._onToggleClick(e)}" />
                </div>
                <span data-testid="${IA_TEST_IDS.toggleLabel}" data-automation-id="${IA_TEST_IDS.toggleLabel}-${isbn10}"
                  >${toggleLabel}</span
                >
              </div>
            </div>
          </div>

          ${this._renderParticipationWarning(this._item, ia)}
        </div>
      </div>
    `;
  }

  private _onToggleClick(event: Event): void {
    event.preventDefault();
    if (!this._item || !this._ia) {
      return;
    }
    if (isToggleDisabled(this._ia) || this._optInOutInFlight) {
      return;
    }
    const selection: OptInOutAction = this._ia.isUserOptedIn ? 'opt-out' : 'opt-in';
    this._emitClick(
      selection === 'opt-out' ? IA_COPY.toggleOptedOut : IA_COPY.toggleOptedIn,
      ANALYTICS_EVENTS.toggle,
      this._getProductAnalytics(this._item, this._ia, selection)
    );
    if (this._ia.isUserOptedIn) {
      this._openOptOutModal(this._item, this._ia);
    } else {
      this._openOptInModal(this._item, this._ia);
    }
  }

  private async _openOptOutModal(item: MyDigitalProductAggregatedItem, ia: InclusiveAccessBlock): Promise<void> {
    const modal: Modal = new Modal({
      header: IA_COPY.optOut.title,
      isHeaderTextCapitalize: false,
      body: { template: this._renderOptOutModalBody(item, ia) },
      primaryButton: { label: IA_COPY.optOut.confirmLabel },
      secondaryButton: { label: IA_COPY.optOut.cancelLabel },
      modalContext: { componentRef: 'ia-opt-out-modal' },
      showClosingCross: true,
      isModalCentered: true,
    });
    let confirmed = false;
    modal.addEventListener('mhecomm-modal-confirmed', async () => {
      if (confirmed) {
        return;
      }
      confirmed = true;
      this._disableModalActions(modal);
      this._emitClick('Confirm', ANALYTICS_EVENTS.modalConfirm);
      await this._confirmOptOut(item, ia, modal);
    });
    await modal.open();
    this._addDismissAnalytics(modal, () => !confirmed);
  }

  private async _confirmOptOut(
    item: MyDigitalProductAggregatedItem,
    ia: InclusiveAccessBlock,
    modal: Modal
  ): Promise<void> {
    this._optInOutInFlight = true;
    this._optInOutError = '';
    try {
      await store
        .dispatch(
          updateMyDigitalProductOptInOut({
            meta: ia.userSectionOptInOutMetaData,
            action: 'opt-out',
          })
        )
        .unwrap();
      this._emitSelectionAnalytics(item, ia, 'opt-out');
    } catch {
      this._optInOutError = GENERIC_API_ERROR;
      this._emitErrorAnalytics(item, ia, 'opt-out');
    } finally {
      this._optInOutInFlight = false;
      modal.close(undefined, false);
    }
  }

  private async _openOptInModal(item: MyDigitalProductAggregatedItem, ia: InclusiveAccessBlock): Promise<void> {
    const modal: Modal = new Modal({
      header: IA_COPY.optIn.title,
      isHeaderTextCapitalize: false,
      body: { template: this._renderOptInModalBody(item, ia) },
      primaryButton: { label: IA_COPY.optIn.confirmLabel },
      secondaryButton: { label: IA_COPY.optIn.cancelLabel },
      modalContext: { componentRef: 'ia-opt-in-modal' },
      showClosingCross: true,
      isModalCentered: true,
    });
    let confirmed = false;
    modal.addEventListener('mhecomm-modal-confirmed', async () => {
      if (confirmed) {
        return;
      }
      confirmed = true;
      this._disableModalActions(modal);
      this._emitClick('Confirm', ANALYTICS_EVENTS.modalConfirm);
      await this._confirmOptIn(item, ia, modal);
    });
    await modal.open();
    this._addDismissAnalytics(modal, () => !confirmed);
  }

  private async _confirmOptIn(
    item: MyDigitalProductAggregatedItem,
    ia: InclusiveAccessBlock,
    modal: Modal
  ): Promise<void> {
    this._optInOutInFlight = true;
    this._optInOutError = '';
    try {
      await store
        .dispatch(
          updateMyDigitalProductOptInOut({
            meta: ia.userSectionOptInOutMetaData,
            action: 'opt-in',
          })
        )
        .unwrap();
      this._emitSelectionAnalytics(item, ia, 'opt-in');
    } catch {
      this._optInOutError = GENERIC_API_ERROR;
      this._emitErrorAnalytics(item, ia, 'opt-in');
    } finally {
      this._optInOutInFlight = false;
      modal.close(undefined, false);
    }
  }

  private async _openWhatIsIAModal(): Promise<void> {
    const [p1, p2, p3] = IA_COPY.whatIsModalBody.split('\n\n');
    const modal: Modal = new Modal({
      header: IA_COPY.whatIsModalTitle,
      body: {
        template: html`
          <div style="max-width: 32rem;">
            <p>${p1}</p>
            <p>${p2}</p>
            <p class="mb-0"><strong>Note:</strong> ${p3?.replace(/^Note:\s*/, '')}</p>
          </div>
        `,
      },
      primaryButton: { label: 'Continue' },
      modalContext: { componentRef: 'what-is-ia-modal' },
      showClosingCross: true,
      isModalCentered: true,
    });
    modal.addEventListener('mhecomm-modal-confirmed', () => modal.close(undefined, false));
    await modal.open();
    this._addDismissAnalytics(modal);
  }

  private _onWhatIsIAClick(event: Event): void {
    const link = event.currentTarget as HTMLButtonElement;
    this._emitClick(link.textContent?.trim() || IA_COPY.whatIsLink, ANALYTICS_EVENTS.infoLink, {
      href: IA_COPY.whatIsLinkHref,
    });
    this._openWhatIsIAModal();
  }

  private _addDismissAnalytics(modal: Modal, canDismiss: () => boolean = () => true): void {
    modal.shadowRoot?.addEventListener(
      'click',
      (event: Event) => {
        const id = (event.composedPath()[0] as HTMLElement | undefined)?.id;
        if (id !== 'modalCloseButton' && id !== 'modal-secondary-button') {
          return;
        }
        event.stopImmediatePropagation();
        if (!canDismiss()) {
          return;
        }
        this._emitClick(
          id === 'modalCloseButton' ? 'Close' : 'Cancel',
          id === 'modalCloseButton' ? ANALYTICS_EVENTS.modalClose : ANALYTICS_EVENTS.modalCancel
        );
        modal.close(undefined, false);
      },
      { capture: true }
    );
  }

  private _disableModalActions(modal: Modal): void {
    modal.shadowRoot
      ?.querySelectorAll<HTMLButtonElement>('#modalCloseButton, #modal-secondary-button, #modal-primary-button')
      .forEach((button) => (button.disabled = true));
  }

  private _emitSelectionAnalytics(
    item: MyDigitalProductAggregatedItem,
    ia: InclusiveAccessBlock,
    selection: OptInOutAction
  ): void {
    this.analyticsEmitter?.(ANALYTICS_EVENTS.selection, {
      eventName: ANALYTICS_EVENTS.selection,
      comp: ANALYTICS_COMPONENT,
      area: ANALYTICS_AREA,
      subarea: ANALYTICS_SUBAREA,
      ...this._getProductAnalytics(item, ia, selection),
    });
  }

  private _emitErrorAnalytics(
    item: MyDigitalProductAggregatedItem,
    ia: InclusiveAccessBlock,
    selection: OptInOutAction
  ): void {
    this.analyticsEmitter?.(ANALYTICS_EVENTS.error, {
      eventName: ANALYTICS_EVENTS.error,
      comp: ANALYTICS_COMPONENT,
      area: ANALYTICS_AREA,
      subarea: ANALYTICS_SUBAREA,
      errorMessage: GENERIC_API_ERROR,
      ...this._getProductAnalytics(item, ia, selection),
    });
  }

  private _emitClick(label: string, eventName: string, detail: Record<string, unknown> = {}): void {
    this.analyticsEmitter?.('click', {
      eventName,
      comp: ANALYTICS_COMPONENT,
      area: ANALYTICS_AREA,
      subarea: ANALYTICS_SUBAREA,
      label,
      ...detail,
    });
  }

  private _onWarningLinkClick(
    event: Event,
    eventName: string,
    item: MyDigitalProductAggregatedItem,
    ia: InclusiveAccessBlock
  ): void {
    const link = event.currentTarget as HTMLAnchorElement;
    this._emitClick(link.textContent?.trim() || '', eventName, {
      href: link.href,
      ...this._getProductAnalytics(item, ia, 'opt-in'),
    });
  }

  private _getProductAnalytics(
    item: MyDigitalProductAggregatedItem,
    ia: InclusiveAccessBlock,
    selection: OptInOutAction
  ): Record<string, unknown> {
    const productTitle = this._nonBlank(item.title?.text) || this._nonBlank(ia.productDescription);
    const itemContributors = this._nonBlankContributors(item.title?.authors);
    const contributors = itemContributors.length ? itemContributors : this._nonBlankContributors(ia.authors);
    const isbn10 = this._nonBlank(item.identity.isbn10);
    const isbn13 = this._nonBlank(item.identity.isbn13);
    const formatType =
      this._nonBlank(item.typeLabel) || this._nonBlank(ia.formatDescription) || this._nonBlank(ia.formatCode);

    return {
      inclusiveAccessSelection: selection,
      ...(productTitle ? { productTitle } : {}),
      ...(contributors?.length ? { contributors } : {}),
      ...(isbn10 ? { isbn10 } : {}),
      ...(isbn13 ? { isbn13 } : {}),
      ...(formatType ? { formatType } : {}),
    };
  }

  private _nonBlank(value?: string | null): string | undefined {
    const normalized = value?.trim();
    return normalized || undefined;
  }

  private _nonBlankContributors(values?: string[] | null): string[] {
    return values?.map((value) => value.trim()).filter(Boolean) ?? [];
  }

  private _renderOptOutModalBody(item: MyDigitalProductAggregatedItem, ia: InclusiveAccessBlock): TemplateResult {
    return html`
      <div class="bg-light p-3 rounded">
        <p class="mb-2">
          ${IA_COPY.optOut.willLose.prefix}<strong>${IA_COPY.optOut.willLose.bold}</strong>${IA_COPY.optOut.willLose
        .suffix}
        </p>
        <ul class="mb-3">
          ${IA_COPY.optOut.willLose.bullets.map((b) => html`<li>${b}</li>`)}
        </ul>
        <p class="mb-2">${IA_COPY.optOut.staysSame.heading}</p>
        <ul class="mb-0">
          ${IA_COPY.optOut.staysSame.bullets.map((b) => html`<li>${b}</li>`)}
        </ul>
      </div>
      <div class="row mt-3 align-items-center">
        <div class="col-4 col-md-3">
          <img
            alt=${ia.productDescription}
            class="img-fluid"
            style="max-width: 96px;"
            src=${getImageSrcUrl(ia.coverImage ? ia.coverImage : ia.productIsbn)} />
        </div>
        <div class="col-8 col-md-9">
          <h4 class="h5 fs-large fw-semibold mb-1">${ia.productDescription}</h4>
          <div>
            <span class="fw-bold">${IA_COPY.optOut.isbn10Label}</span> ${item.identity.isbn10}
            ${item.identity.isbn13
        ? html` | <span class="fw-bold">${IA_COPY.optOut.isbn13Label}</span> ${item.identity.isbn13}`
        : nothing}
          </div>
          ${ia.formatDescription
        ? html`<div><span class="fw-bold">${IA_COPY.optOut.formatLabel}</span> ${ia.formatDescription}</div>`
        : nothing}
        </div>
      </div>
    `;
  }

  private _renderOptInModalBody(item: MyDigitalProductAggregatedItem, ia: InclusiveAccessBlock): TemplateResult {
    const formattedPrice = this._formatPrice(ia);
    return html`
      <style>
        .ia-opt-in-modal {
          width: min(29rem, calc(100vw - 3rem));
        }

        .ia-opt-in-info {
          background-color: #f2f2f2;
          border: 1px solid #dee2e6;
          border-radius: 0.25rem;
        }

        .ia-opt-in-product {
          display: grid;
          grid-template-columns: 3.25rem minmax(0, 1fr);
          gap: 1rem;
        }

        .ia-opt-in-product img {
          max-height: 4.5rem;
          width: 3.25rem;
          object-fit: contain;
        }

        .ia-opt-in-price {
          border: 1px solid #dee2e6;
          border-radius: 0.5rem;
        }
      </style>
      <div class="ia-opt-in-modal">
        <div class="ia-opt-in-info p-2 mb-3">
          <ul class="mb-3 ps-4">
            ${IA_COPY.optIn.benefits.map((benefit) => html`<li>${benefit}</li>`)}
          </ul>
          <p>
            If you previously purchased these required course materials individually, visit
            <a href="${environment().accountUrl}/order-history.html">${IA_COPY.optIn.orderHistoryLabel}</a>
            to request a refund.
          </p>
          <p class="mb-0">
            If you have further questions, review the McGraw Hill
            <a href="${environment().returnPolicyUrl}">${IA_COPY.optIn.refundPolicyLabel}</a>.
          </p>
        </div>

        <div class="ia-opt-in-product align-items-start px-2">
          <img
            alt=${ia.productDescription}
            src=${getImageSrcUrl(ia.coverImage ? ia.coverImage : ia.productIsbn)} />
          <div>
            <h4 class="h6 fw-bold mb-2">${ia.productDescription}</h4>
            <div>
              <span class="fw-bold">${IA_COPY.optOut.isbn10Label}</span> ${item.identity.isbn10}
              ${item.identity.isbn13
        ? html` | <span class="fw-bold">${IA_COPY.optOut.isbn13Label}</span> ${item.identity.isbn13}`
        : nothing}
            </div>
            ${ia.formatDescription
        ? html`<div><span class="fw-bold">${IA_COPY.optOut.formatLabel}</span> ${ia.formatDescription}</div>`
        : nothing}
          </div>
        </div>

        ${formattedPrice
        ? html`<div
              class="ia-opt-in-price d-flex justify-content-between align-items-center p-3 mt-3"
              data-testid="${IA_TEST_IDS.optInPrice}">
              <span class="fw-semibold">${IA_COPY.optIn.priceLabel}</span>
              <span class="fw-bold">${formattedPrice}</span>
            </div>`
        : nothing}
      </div>
    `;
  }

  private _renderParticipationWarning(
    item: MyDigitalProductAggregatedItem,
    ia: InclusiveAccessBlock
  ): TemplateResult | typeof nothing {
    if (this._optInOutInFlight) {
      return nothing;
    }
    const hasProductLicense = this._hasProductLicense(item);
    if (ia.isUserOptedIn === false && hasProductLicense === false) {
      return html`<div
        class="alert alert-warning d-flex align-items-start gap-2 mt-3 mb-0"
        role="status"
        data-testid="${IA_TEST_IDS.optedOutWarning}"
        data-automation-id="${IA_TEST_IDS.optedOutWarning}-${this.isbn10}">
        ${this.uxtIconReady ? html`<uxt-icon icon="exclamation-triangle" aria-hidden="true"></uxt-icon>` : nothing}
        <div>
          <strong>${IA_COPY.warning.optedOutTitle}</strong> ${IA_COPY.warning.optedOutBody}
        </div>
      </div>`;
    }
    if (ia.isUserOptedIn === true) {
      return html`<div
        class="alert alert-warning d-flex align-items-start gap-2 mt-3 mb-0"
        role="status"
        data-testid="${IA_TEST_IDS.optedInWarning}"
        data-automation-id="${IA_TEST_IDS.optedInWarning}-${this.isbn10}">
        ${this.uxtIconReady ? html`<uxt-icon icon="exclamation-triangle" aria-hidden="true"></uxt-icon>` : nothing}
        <div>
          <strong>${IA_COPY.warning.optedInTitle}</strong> ${IA_COPY.warning.optedInBeforeOrderHistory}<a
            href="${environment().accountUrl}/order-history.html"
            @click=${(event: Event) =>
              this._onWarningLinkClick(
                event,
                ANALYTICS_EVENTS.orderHistoryLink,
                item,
                ia
              )}
            >${IA_COPY.optIn.orderHistoryLabel}</a
          >${IA_COPY.warning.optedInAfterOrderHistory}<a
            href="${environment().returnPolicyUrl}"
            @click=${(event: Event) =>
              this._onWarningLinkClick(
                event,
                ANALYTICS_EVENTS.refundPolicyLink,
                item,
                ia
              )}
            >${IA_COPY.warning.refundPolicyLabel}</a
          >.
        </div>
      </div>`;
    }
    return nothing;
  }

  private _hasProductLicense(item: MyDigitalProductAggregatedItem): boolean | undefined {
    if (!item.expiry) {
      return undefined;
    }
    return Boolean(item.expiry.licenseEndDate);
  }

  private _formatPrice(ia: InclusiveAccessBlock): string | null {
    const rawPrice = ia.userSectionOptInOutMetaData.price;
    if (rawPrice === undefined || rawPrice === null || String(rawPrice).trim() === '') {
      return null;
    }
    const price = typeof rawPrice === 'number' ? rawPrice : Number(rawPrice);
    if (!Number.isFinite(price) || price < 0) {
      return null;
    }
    const suppliedCurrencyCode = ia.userSectionOptInOutMetaData.currencyCode?.trim().toUpperCase();
    const countryCode = ia.userSectionOptInOutMetaData.countryCode?.trim().toUpperCase();
    if (!suppliedCurrencyCode || !/^[A-Z]{3}$/.test(suppliedCurrencyCode)) {
      return null;
    }
    if (!countryCode || !/^[A-Z]{2}$/.test(countryCode)) {
      return null;
    }
    try {
      return new Intl.NumberFormat(`en-${countryCode}`, {
        style: 'currency',
        currency: suppliedCurrencyCode,
      }).format(price);
    } catch {
      return null;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-inclusive-access-panel': InclusiveAccessPanel;
  }
}
